<script setup lang="ts">
import { ref, computed, onBeforeMount, onMounted, defineAsyncComponent, watch } from 'vue'
import { useCommonStore } from '~/stores/common'
import { useRoute, useRouter } from 'vue-router'
import { definePageMeta, useSeoMeta, showError, useHead } from '~/.nuxt/imports'
import Breadcrumbs from '~/components/Breadcrumbs.vue'
import Team from '~/components/about/Team.vue'
const Content = defineAsyncComponent(() => import(`@/components/management/${managerCode.value}.vue`))

const commonStore = useCommonStore()

const route = useRoute()

definePageMeta({
    layout: 'main',
    middleware: ['auth']
})

const managerCode = computed(() => route.params.management_id as string)
const filteredManagement = computed(() => {
    return Object.keys(management.value).filter(key => key !== managerCode.value).reduce((sum, key) => {
        return Object.assign(sum, {
            [key]: management.value[key]
        })
    }, {})
})

const management = ref<any>({
    oscarhartmann: {
        name: 'Оскар Хартманн',
        post: 'Президент компании Aktivo'
    },
    egorklimenko: {
        name: 'Егор Клименко',
        post: 'Генеральный директор Aktivo',
    },
    konstantinzemskov: {
        name: 'Константин Земсков',
        post: 'Заместитель Генерального директора'
    },
    mihailkostromin: {
        name: 'Михаил Костромин',
        post: 'Коммерческий директор AKTIVO'
    }
})

useSeoMeta({
    title: () => `${management.value[managerCode.value]?.name} — AKTIVO`,
    description: () => `Менеджмент компании AKTIVO. ${management.value[managerCode.value]?.name}. ${management.value[managerCode.value]?.post}.`
})

onBeforeMount(() => {
    if (!management.value[managerCode.value]) {
        showError({
            statusCode: '404',
            message: 'Страница не найдена'
        })
    }
})

onMounted(() => {
    commonStore.setTheme('white')
    commonStore.setLoadingStatus(false)
})
</script>

<template>
    <Breadcrumbs  :data="[{ name: 'О компании', link: '/about' }, { name: 'Менеджмент компании AKTIVO', link: '/management' }, { name: management[managerCode].name }]" />
    <section class="manager">
        <div class="container">
            <h1 class="manager__title">{{ management[managerCode].name }}</h1>
            <div class="manager__subtitle">{{ management[managerCode].post }}</div>
            <div class="manager__content">
                <div class="manager__image">
                    <NuxtImg
                        :src="`/img/management/${managerCode}.jpg`"
                        alt="Константин Земсков"
                        loading="lazy"
                        placeholder="/img/loading.gif"
                        placeholder-class="loading"
                    />
                </div>
                <Content />
            </div>
        </div>
    </section>
    <Team :data="filteredManagement" />
</template>

<style lang="scss">
.manager {
    padding-top: calc(65px + 54px);
    margin-top: calc(-65px - 54px);
    background-color: #E1E5EA;

    &__content {
        position: relative;
    }

    &__title {
        font-size: 16px;
        line-height: 1.25;
        font-family: 'Muller', sans-serif;
        font-weight: 800;
        text-transform: uppercase;
        color: #162133;
        margin-top: 0;
        margin-bottom: 8px;
    }

    &__subtitle {
        color: #162133;
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        margin-bottom: 16px;
    }

    &__image {
        width: 100%;
        height: 404px;
        border-radius: 24px;
        overflow: hidden;
        margin-bottom: 8px;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top;
        }
    }

    &__item {
        padding: 16px;
        border-radius: 24px;
        background: white;
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
        }

        a {
            color: var(--theme-color);
            text-decoration: none;
        }
    }    

    &__item-title {
        color: #162133;
        font-size: 16px;
        font-weight: 600;
        line-height: 125%; 
        margin-bottom: 8px;
    }

    &__item-text {
        color: #162133;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__career {
        .career {
            position: relative;

            &::before {
                position: absolute;
                content: '';
                height: 100%;
                width: 1px;
                top: 0;
                left: 1.5px;
                background: #B8BFC9;
            }

            &__item {
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;

                    .career__year {
                        align-self: stretch;
                    }
                }
            }

            &__year {
                position: relative;
                padding-left: 13px;
                width: 113px;
                min-width: 113px;
                margin-right: 24px;
                color: #162133;
                font-size: 14px;
                font-weight: 700;
                line-height: 140%;
                background: white;

                &::before {
                    position: absolute;
                    content: '';
                    top: 8px;
                    left: 0;
                    width: 4px;
                    height: 4px;
                    background: #B8BFC9;
                    border-radius: 50%;
                }
            }

            &__description {
                flex: 1;

                p {
                    color: #162133;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: normal;
                    margin-bottom: 16px;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }
        }
    }

    &__list {
        .list {
            list-style-type: none;

            &__item {
                position: relative;
                font-size: 12px;
                font-weight: 500;
                line-height: normal;
                margin-bottom: 16px;
                padding-left: 20px;

                &:last-child {
                    margin-bottom: 0;
                }

                &::before {
                    position: absolute;
                    content: '';
                    width: 4px;
                    height: 100%;
                    top: 0;
                    left: 0;
                    border-radius: 0px 5px 5px 0px;
                    background: #B8BFC9;
                }
            }
        }
    }

    &__quote {
        .quote {
            position: relative;
            padding: 32px 8px 32px 12px;
            border: 1px solid #B8BFC9;
            border-radius: 8px;

            &::before {
                position: absolute;
                content: '';
                width: 4px;
                height: calc(100% - 18px);
                top: 8px;
                left: 0;
                border-radius: 0px 10px 10px 0px;
                background: #B8BFC9;
            }

            &__text {
                p {
                    color: #162133;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: normal;
                    margin-bottom: 16px;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    &.small {
                        font-size: 12px;
                        font-weight: 500;
                        line-height: normal; 
                    }
                }
            }

            &__icon {
                position: absolute;
                width: 24px;
                height: 16px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:first-child {
                    top: 8px;
                    left: 12px;
                }

                &:last-child {
                    bottom: 8px;
                    right: 8px;
                }
            }
        }
    }
}

@media screen and (min-width: 767.98px) {
    .manager {
        &__title {
            font-size: 40px;
            font-weight: 700;
            line-height: 100%;
            text-transform: none;
        }

        &__subtitle {
            font-size: 16px;
            font-weight: 500;
            line-height: 140%;
            margin-bottom: 24px;
        }

        &__image {
            height: 736px;
            border-radius: 40px;
            margin-bottom: 16px;
        }

        &__item {
            padding: 32px;
            border-radius: 40px;
            margin-bottom: 16px;
        }

        &__item-title {
            font-size: 24px;
            line-height: normal;
            margin-bottom: 24px;
        }

        &__item-text {
            font-size: 16px;
            line-height: 140%;
            margin-bottom: 24px;
        }

        &__career {
            .career {
                &__item {
                    margin-bottom: 24px;
                }

                &__year {
                    font-size: 16px;
                    line-height: 140%;
                    width: 133px;

                    &::before {
                        top: 9px;
                    }
                }

                &__description {
                    p {
                        font-size: 16px;
                        font-weight: 500;
                        line-height: 140%;
                        margin-bottom: 24px;
                    }
                }
            }
        }

        &__list {
            .list {
                &__item {
                    font-size: 16px;
                    line-height: 140%; 
                }
            }
        }

        &__quote {
            .quote {
                padding: 40px 16px 40px 20px;

                &__text {
                    p {
                        font-size: 18px;
                        font-weight: 600;
                        line-height: normal;

                        &.small {
                            font-size: 14px;
                            line-height: 140%; 
                        }
                    }
                }

                &__icon {
                    &:first-child {
                        top: 16px;
                        left: 20px;
                    }

                    &:last-child {
                        right: 16px;
                        bottom: 16px;
                    }
                }
            }
        }
    }
}

@media screen and (min-width: 1199.98px) {
    .manager {
        &__content {
            display: flex;
            flex-direction: row-reverse;
            align-items: flex-start;
            justify-content: flex-start;
        }

        &__image {
            position: sticky;
            width: 576px;
            height: 576px;
            margin-bottom: 0;
            top: 48px;
        }

        &__info {
            width: calc(100% - 576px);
            margin-right: 16px;
        }

        &__item {
            padding: 40px;
        }
    }
}

@media screen and (min-width: 1360px) {
    .manager {
        padding-top: calc(65px + 100px);
        margin-top: calc(-65px - 100px);

        &__subtitle {
            margin-bottom: 40px;
        }

        &__image {
            width: 652px;
            height: 652px;
        }

        &__info {
            width: calc(100% - 652px);
            margin-right: 16px;
        }
    }
}
</style>